import { useEffect } from "react";

/**
 * Measures the sticky navbar and publishes its height as `--bl-navbar-h` on the
 * document root.
 *
 * The navbar is `<header class="sticky-top">` and its height comes from its
 * content, so it differs between breakpoints and would change again if the logo
 * were resized. Anything that has to sit directly under it -- a sticky rail, a
 * `scroll-margin-top`, a full-height pane -- needs the real number, and needs
 * the *same* number, or the two drift apart.
 *
 * Published on `documentElement` rather than a page-level ref. It was scoped to
 * a ref first, which broke as soon as a caller rendered a loading branch and a
 * loaded branch: those are different elements, the effect only ran against the
 * first one, and the value vanished with it when the content arrived. The
 * navbar's height is a fact about the document, so that is where it belongs.
 */
export const useNavbarHeight = () => {
  useEffect(() => {
    const header = document.querySelector("header.sticky-top");
    if (!header) return;

    const root = document.documentElement;

    // Floor, and the direction is deliberate. Consumers subtract this from
    // 100vh, so the two failure modes are not symmetric:
    //
    //   over-report  -> the element is a fraction SHORT -> a sub-pixel strip of
    //                   <body> shows through, and Console.css paints body
    //                   #ebebeb, so it reads as a thin white line
    //   under-report -> the element is a fraction TALL -> overflow, which the
    //                   pages using this already clip
    //
    // Under-reporting used to mean a stray document scrollbar, which is why
    // this rounded up at first. Post.jsx now locks body overflow while its
    // split layout is active, so the overflow is absorbed and only the visible
    // failure is left to avoid.
    const apply = () =>
      root.style.setProperty(
        "--bl-navbar-h",
        `${Math.floor(header.getBoundingClientRect().height)}px`,
      );

    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(header);
    return () => {
      observer.disconnect();
      root.style.removeProperty("--bl-navbar-h");
    };
  }, []);
};
